import { ApplicationToComponentStrategyResolver }
  from '~/modules/Products/Infrastructure/ApplicationToComponentStrategies/ApplicationToComponentStrategyResolver'
import { ApplicationToComponentTrophyStrategy }
  from '~/modules/Products/Infrastructure/ApplicationToComponentStrategies/ApplicationToComponentTrophyStrategy'
import { ApplicationToComponentCardStrategy }
  from '~/modules/Products/Infrastructure/ApplicationToComponentStrategies/ApplicationToComponentCardStrategy'
import type { NextPage } from 'next'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { useTranslation } from 'next-i18next'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import nextI18nextConfig from 'next-i18next.config'
import { getServerSession } from 'next-auth/next'
import { authOptions } from '~/pages/api/auth/[...nextauth]'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { DependencyInjector } from '~/injector/dependency.injector'
import { CardRepositoryInterface } from '~/modules/Cards/Domain/CardRepositoryInterface'
import { GetMyCollection } from '~/modules/Products/Application/GetMyCollection'
import { ApplicationProductSorter } from '~/modules/Products/Application/ApplicationProductSorter'
import { ApplicationProductDto } from '~/modules/Products/Application/ApplicationProductDto'
import { MyCollection } from '~/modules/Products/Infrastructure/Components/MyCollection/MyCollection'
import { CardCollectionApplicationDto } from '~/modules/CardCollections/Application/GetCardCollectionsApplicationDto'
import { GetCardCollections } from '~/modules/CardCollections/Application/GetCardCollections'
import {
  CardCollectionComponentDtoTranslator
} from '~/modules/CardCollections/Infrastructure/CardCollectionComponentDtoTranslator'
import { CardCollectionRepositoryInterface } from '~/modules/CardCollections/Domain/CardCollectionRepositoryInterface'
import { ProfileDto } from '~/modules/Profile/ProfileDto'
import { ProfileRepositoryInterface } from '~/modules/Profile/ProfileRepositoryInterface'
import { GetProfile } from '~/modules/Profile/GetProfile'

interface Props {
  profile: ProfileDto
  cardCollections: CardCollectionApplicationDto[]
  products: ApplicationProductDto[]
  htmlPageMetaContextProps: HtmlPageMetaContextProps
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const htmlPageMetaContextService = new HtmlPageMetaContextService(context)
  const session = await getServerSession(context.req, context.res, authOptions)

  if (session && session.user) {
    const userId: string = session.user.id

    const dependencyInjector = DependencyInjector.getInstance()

    const profileRepository: ProfileRepositoryInterface = dependencyInjector.resolve('ProfileRepositoryInterface')
    const trophyRepository: TrophyRepositoryInterface = dependencyInjector.resolve('TrophyRepositoryInterface')
    const cardRepository: CardRepositoryInterface = dependencyInjector.resolve('CardRepositoryInterface')
    const cardCollectionRepository: CardCollectionRepositoryInterface = dependencyInjector
      .resolve('CardCollectionRepositoryInterface')

    const cardCollectionsGetter = new GetCardCollections(cardCollectionRepository)
    const sorterService = new ApplicationProductSorter()
    const getMyCollection = new GetMyCollection(trophyRepository, cardRepository, sorterService)
    const getProfile = new GetProfile(profileRepository)

    const products = await getMyCollection.get({ userId })
    const cardCollections = await cardCollectionsGetter.get(userId)
    const profile = await getProfile.get(userId)

    const props: Props = {
      profile,
      cardCollections: JSON.parse(JSON.stringify(cardCollections.cardCollections)),
      products: JSON.parse(JSON.stringify(products)),
      htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
      ...await serverSideTranslations(locale, [
        'user_tos',
        'app_menu',
        'card_collections',
        'cards',
        'footer',
        'trophies',
        'wallet_error_modal',
        'my_collection',
        'card_collection_list',
        'market_banner',
      ]),
    }

    return { props }
  } else {
    return {
      redirect: {
        destination: `/${locale}/auth/signin?callbackUrl=/my-collection`,
        permanent: false,
      },
    }
  }
}

const MyCollectionPage: NextPage<Props> = (props) => {
  const { t } = useTranslation('my_collection')

  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(t('my_collection_page_title'), t('my_collection_page_description'))
  ).getProperties()
  const htmlPageMetaProps = { ...props.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  const productResolver = new ApplicationToComponentStrategyResolver([
    new ApplicationToComponentCardStrategy(),
    new ApplicationToComponentTrophyStrategy(),
  ])

  const componentProducts = props.products.map((product) => productResolver.resolve(product))
  const cardCollections = props.cardCollections.map(CardCollectionComponentDtoTranslator.fromApplication)

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <MyCollection
        profile={ props.profile }
        products={ componentProducts }
        cardCollections={ cardCollections }
      />
    </>
  )
}

export default MyCollectionPage
